import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import ViewListIcon from "@material-ui/icons/ViewList";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import WarningIcon from "@material-ui/icons/WarningRounded";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import { dayKey, groupByDay, scheduleState } from "../../helpers/scheduleStatus";
import ScheduleCard from "./ScheduleCard";
import ScheduleCalendar from "./ScheduleCalendar";
import DayPanel from "./DayPanel";

const PAGE_SIZE = 20;
const VIEW_STORAGE_KEY = "schedules:viewMode";

const TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "attention", label: "Precisam de atenção" },
  { key: "sent", label: "Enviadas" },
  { key: "all", label: "Todas" },
];

const EMPTY_TEXT = {
  pending: "Nenhuma mensagem pendente",
  attention: "Nenhuma mensagem precisa de atenção",
  sent: "Nenhuma mensagem enviada ainda",
  all: "Nenhuma mensagem agendada",
};

const getUrlParam = (param) => new URLSearchParams(window.location.search).get(param);

const readStoredView = () => {
  try {
    const value = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return value === "calendar" ? "calendar" : "list";
  } catch (e) {
    return "list";
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.text.primary,
    padding: theme.spacing(3),
    maxWidth: 960,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  rootWide: { maxWidth: 1280 },
  headerRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  title: { fontSize: 26, fontWeight: 700, lineHeight: 1.2 },
  summary: { marginTop: 4, fontSize: 13, color: theme.palette.text.secondary },
  summaryAttention: {
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#FF8A80" : "#C62828",
  },
  headerActions: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  viewToggle: {
    display: "flex",
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  viewButton: {
    height: 34,
    padding: theme.spacing(0, 1.75),
    border: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    color: theme.palette.text.secondary,
    font: "600 13px Inter, Roboto, sans-serif",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  viewButtonActive: {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
  },
  addButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
  },
  search: {
    width: "100%",
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
  },
  tabs: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      overflowX: "auto",
      flexWrap: "nowrap",
      paddingBottom: theme.spacing(0.5),
    },
    flexWrap: "wrap",
  },
  tab: {
    flexShrink: 0,
    height: 32,
    padding: theme.spacing(0, 1.75),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    font: "500 13px Inter, Roboto, sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  tabActive: {
    backgroundColor: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    fontWeight: 600,
  },
  tabAttention: {
    borderColor: "rgba(229,57,53,0.5)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(229,57,53,0.14)" : "#FDECEC",
    color: theme.palette.type === "dark" ? "#FF8A80" : "#B3261E",
    fontWeight: 600,
  },
  tabCount: { opacity: 0.75, fontWeight: 500 },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: theme.spacing(2.5, 0, 1),
    color: theme.palette.text.secondary,
    font: "600 12px Inter, Roboto, sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    "&::after": { content: '""', flex: 1, height: 1, backgroundColor: theme.palette.divider },
  },
  groupHeaderAttention: { color: theme.palette.type === "dark" ? "#FF8A80" : "#B3261E" },
  groupCount: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    boxSizing: "border-box",
    borderRadius: 999,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: 0,
  },
  stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  loadMore: { display: "flex", justifyContent: "center", margin: theme.spacing(2, 0) },
  center: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  calendarLayout: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "stretch",
    [theme.breakpoints.down("md")]: { flexDirection: "column" },
  },
  calendarPaper: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2),
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 230px)",
    minHeight: 560,
    "& > div:first-child": { flex: 1, minHeight: 0 },
  },
  calendarOptions: { display: "flex", justifyContent: "flex-end", marginBottom: theme.spacing(0.5) },
}));

const nextLabel = (date) => {
  if (!date) return "";
  const m = moment(date);
  const key = dayKey(date);
  let day;
  if (key === dayKey(new Date())) day = "hoje";
  else if (key === dayKey(moment().add(1, "day").toDate())) day = "amanhã";
  else day = m.format("DD/MM");
  return `próxima ${day} às ${m.format("HH:mm")}`;
};

const Schedules = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [viewMode, setViewMode] = useState(readStoredView);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [items, setItems] = useState([]);
  const [attentionItems, setAttentionItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, attention: 0, sent: 0, all: 0 });
  const [nextSendAt, setNextSendAt] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [calendarItems, setCalendarItems] = useState([]);
  const [showTasks, setShowTasks] = useState(false);
  const [tasks, setTasks] = useState([]);

  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sendingNow, setSendingNow] = useState(null);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [selectedDay, setSelectedDay] = useState(() => new Date());

  const requestId = useRef(0);

  const changeView = (mode) => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch (e) {
      // sem armazenamento local: a escolha vale só nesta visita
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Busca uma página. `replace` troca a lista inteira; senão acrescenta.
  const fetchList = useCallback(
    async ({ pageNumber, replace }) => {
      const id = ++requestId.current;
      if (pageNumber > 1) setLoadingMore(true);
      try {
        const { data } = await api.get("/schedules/", {
          params: {
            view: tab,
            searchParam: debouncedSearch,
            pageNumber,
            pageSize: PAGE_SIZE,
          },
        });
        if (id !== requestId.current) return; // resposta de uma busca antiga
        setItems((prev) => {
          if (replace) return data.schedules;
          const known = new Set(prev.map((s) => s.id));
          return [...prev, ...data.schedules.filter((s) => !known.has(s.id))];
        });
        setHasMore(data.hasMore);
        setCounts(data.counts || { pending: 0, attention: 0, sent: 0, all: 0 });
        setNextSendAt(data.nextSendAt || null);

        // Na aba "Pendentes", os que precisam de atenção aparecem no topo
        if (tab === "pending" && data.counts?.attention > 0) {
          const { data: att } = await api.get("/schedules/", {
            params: { view: "attention", searchParam: debouncedSearch, pageSize: 50 },
          });
          if (id === requestId.current) setAttentionItems(att.schedules);
        } else if (id === requestId.current) {
          setAttentionItems([]);
        }
      } catch (err) {
        if (id === requestId.current) toastError(err);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tab, debouncedSearch]
  );

  const fetchCalendar = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules/", {
        params: { view: "all", pageNumber: 1, pageSize: 9999 },
      });
      setCalendarItems(data.schedules);
      setCounts((prev) => data.counts || prev);
      setNextSendAt(data.nextSendAt || null);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const refresh = useCallback(() => {
    if (viewMode === "calendar") {
      fetchCalendar();
    } else {
      setPage(1);
      fetchList({ pageNumber: 1, replace: true });
    }
  }, [viewMode, fetchCalendar, fetchList]);

  // Trocar de aba/busca volta para a primeira página
  useEffect(() => {
    if (viewMode !== "list") return;
    setLoading(true);
    setItems([]);
    setPage(1);
    fetchList({ pageNumber: 1, replace: true });
  }, [viewMode, fetchList]);

  useEffect(() => {
    if (viewMode === "calendar") fetchCalendar();
  }, [viewMode, fetchCalendar]);

  useEffect(() => {
    if (!showTasks) {
      setTasks([]);
      return;
    }
    api
      .get("/tasks")
      .then(({ data }) => setTasks(data))
      .catch(() => {});
  }, [showTasks]);

  // Atualização em tempo real + releitura a cada minuto: uma mensagem
  // pendente passa para "precisa de atenção" só pelo passar do tempo.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    let timer = null;
    socket.on(`company-${user.companyId}-schedule`, () => {
      clearTimeout(timer);
      timer = setTimeout(() => refreshRef.current(), 300);
    });
    const interval = setInterval(() => refreshRef.current(), 60000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
      socket.disconnect();
    };
  }, [user.companyId]);

  // Vindo da ficha do contato (?contactId=): abre o modal já com o contato
  useEffect(() => {
    if (contactId) openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = (date = null) => {
    setSelectedSchedule(null);
    setDuplicateSource(null);
    setSelectedDate(date);
    setModalOpen(true);
  };

  // Novo agendamento a partir do dia escolhido no calendário: 09:00 do dia
  // (ou daqui a 1 hora, se hoje já passou das 09:00)
  const openNewOnDay = () => {
    let when = moment(selectedDay).hour(9).minute(0).second(0);
    if (when.isBefore(moment())) when = moment().add(1, "hour");
    openNew(when.toDate());
  };

  const openDuplicate = (schedule) => {
    setSelectedSchedule(null);
    setSelectedDate(null);
    setDuplicateSource(schedule);
    setModalOpen(true);
  };

  const openSchedule = (schedule) => {
    setDuplicateSource(null);
    setSelectedSchedule(schedule);
    setSelectedDate(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedSchedule(null);
    setSelectedDate(null);
    setDuplicateSource(null);
    setModalOpen(false);
  };

  const handleSendNow = async () => {
    const target = sendingNow;
    setSendingNow(null);
    try {
      await api.post(`/schedules/${target.id}/send-now`);
      toast.success("Mensagem na fila de envio.");
      refresh();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    const target = deleting;
    setDeleting(null);
    try {
      await api.delete(`/schedules/${target.id}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
      refresh();
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchList({ pageNumber: next, replace: false });
  };

  const groups = useMemo(() => groupByDay(items), [items]);

  const renderCard = (schedule) => (
    <ScheduleCard
      key={schedule.id}
      schedule={schedule}
      state={scheduleState(schedule)}
      onOpen={openSchedule}
      onDelete={setDeleting}
      onSendNow={setSendingNow}
      onDuplicate={openDuplicate}
    />
  );

  const attentionCount = counts.attention;
  const showAttentionGroup = tab === "pending" && attentionItems.length > 0;
  const listIsEmpty = !loading && items.length === 0 && !showAttentionGroup;

  return (
    <div className={`${classes.root} ${viewMode === "calendar" ? classes.rootWide : ""}`}>
      <ConfirmationModal
        title={i18n.t("schedules.confirmationModal.deleteTitle")}
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title="Enviar agora"
        open={Boolean(sendingNow)}
        onClose={() => setSendingNow(null)}
        onConfirm={handleSendNow}
      >
        {`A mensagem para ${sendingNow?.contact?.name || "o contato"} será enviada agora, sem esperar o horário agendado.`}
      </ConfirmationModal>

      <ScheduleModal
        open={modalOpen}
        onClose={closeModal}
        reload={refresh}
        scheduleId={selectedSchedule?.id}
        contactId={contactId}
        cleanContact={() => setContactId("")}
        selectedDate={selectedDate}
        duplicateFrom={duplicateSource}
      />

      <div className={classes.headerRow}>
        <div>
          <Typography component="h1" className={classes.title}>
            {i18n.t("schedules.title")}
          </Typography>
          <Typography className={classes.summary}>
            {counts.pending} pendente{counts.pending !== 1 ? "s" : ""}
            {attentionCount > 0 && (
              <span className={classes.summaryAttention}>
                {" "}
                · {attentionCount} precisa{attentionCount !== 1 ? "m" : ""} de atenção
              </span>
            )}
            {nextSendAt ? ` · ${nextLabel(nextSendAt)}` : ""}
          </Typography>
        </div>
        <div className={classes.headerActions}>
          <div className={classes.viewToggle} role="group" aria-label="Modo de visualização">
            <button
              type="button"
              aria-pressed={viewMode === "list"}
              className={`${classes.viewButton} ${viewMode === "list" ? classes.viewButtonActive : ""}`}
              onClick={() => changeView("list")}
            >
              <ViewListIcon style={{ fontSize: 18 }} />
              Lista
            </button>
            <button
              type="button"
              aria-pressed={viewMode === "calendar"}
              className={`${classes.viewButton} ${viewMode === "calendar" ? classes.viewButtonActive : ""}`}
              onClick={() => changeView("calendar")}
            >
              <CalendarTodayIcon style={{ fontSize: 16 }} />
              Calendário
            </button>
          </div>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={() => openNew()}
          >
            Nova mensagem
          </Button>
        </div>
      </div>

      {viewMode === "calendar" ? (
        <>
          <div className={classes.calendarOptions}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={showTasks}
                  onChange={(e) => setShowTasks(e.target.checked)}
                />
              }
              label="Incluir tarefas"
            />
          </div>
          <div className={classes.calendarLayout}>
            <Paper variant="outlined" className={classes.calendarPaper}>
              <ScheduleCalendar
                schedules={calendarItems}
                tasks={tasks}
                showTasks={showTasks}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onSelectSchedule={openSchedule}
                onCreateAt={(date) => openNew(date)}
              />
            </Paper>
            <DayPanel
              day={selectedDay}
              schedules={calendarItems.filter((x) => dayKey(x.sendAt) === dayKey(selectedDay))}
              onOpen={openSchedule}
              onCreate={openNewOnDay}
            />
          </div>
        </>
      ) : (
        <>
          <TextField
            className={classes.search}
            placeholder="Buscar por contato, número ou mensagem"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <div className={classes.tabs} role="tablist">
            {TABS.map(({ key, label }) => {
              const active = tab === key;
              const alert = key === "attention" && counts.attention > 0 && !active;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`${classes.tab} ${active ? classes.tabActive : ""} ${
                    alert ? classes.tabAttention : ""
                  }`}
                  onClick={() => setTab(key)}
                >
                  {label}
                  <span className={classes.tabCount}>{counts[key]}</span>
                </button>
              );
            })}
          </div>

          {loading && items.length === 0 ? (
            <div className={classes.center}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <>
              {showAttentionGroup && (
                <>
                  <div className={`${classes.groupHeader} ${classes.groupHeaderAttention}`}>
                    <WarningIcon style={{ fontSize: 15 }} />
                    Precisam de atenção
                    <span className={classes.groupCount}>{attentionCount}</span>
                  </div>
                  <div className={classes.stack}>{attentionItems.map(renderCard)}</div>
                </>
              )}

              {tab === "attention" ? (
                <div className={classes.stack} style={{ marginTop: 16 }}>
                  {items.map(renderCard)}
                </div>
              ) : (
                groups.map((group) => (
                  <React.Fragment key={group.key}>
                    <div className={classes.groupHeader}>
                      {group.label}
                      <span className={classes.groupCount}>{group.items.length}</span>
                    </div>
                    <div className={classes.stack}>{group.items.map(renderCard)}</div>
                  </React.Fragment>
                ))
              )}

              {listIsEmpty && (
                <div className={classes.empty}>
                  <EventBusyIcon style={{ fontSize: 48, opacity: 0.35 }} />
                  <Typography>
                    {debouncedSearch ? "Nada encontrado para essa busca" : EMPTY_TEXT[tab]}
                  </Typography>
                  {tab === "pending" && !debouncedSearch && (
                    <Button color="primary" onClick={() => openNew()}>
                      Agendar uma mensagem
                    </Button>
                  )}
                </div>
              )}

              {hasMore && (
                <div className={classes.loadMore}>
                  <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? <CircularProgress size={18} /> : "Carregar mais"}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Schedules;
